import { useCallback, useEffect, useMemo, useState } from "react";
import { Formik, Form, Field as FormikField, FieldArray, getIn } from "formik";
import { useSnackbar } from "notistack";
import { createPlanAdmin, getAllPlansAdmin, getSinglePlansAdmin, updatePlanAdmin, removePackage } from "../shared/_services/common.services";
import { getBillingCatalog } from "../shared/_services/billing.services";
import {
    ADMIN_APPLE_PRODUCT,
    ADMIN_DESCRIPTION,
    ADMIN_NEEDS_ATTENTION,
    ADMIN_NOTICE_APPLE_INTRO_UNVERIFIED,
    ADMIN_NOTICE_APPLE_MISSING,
    ADMIN_NOTICE_CURRENCY_MISMATCH,
    ADMIN_NOTICE_GENERIC,
    ADMIN_NOTICE_STRIPE_TAX_UNCHECKED,
    ADMIN_NOTICE_STRIPE_UNREACHABLE,
    ADMIN_NOTICE_OLDER_PRICE,
    ADMIN_NOTICE_WEBHOOK,
    ADMIN_NOTICES_TITLE,
    ADMIN_STATUS_REASONS,
    ADMIN_STRIPE_STATUS,
    ADMIN_SYNCED,
    ADMIN_TIER,
    ADMIN_TIER_NONE,
    ADMIN_TIERS,
} from "./plan/planCopy";
import Loader from "./Loader";
import StarsIcon from "../styles/StarsIcon";
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
    Field,
    Input,
    Page,
    PageHead,
    Panel,
    Select,
} from "./ui";

const createInitialFormState = () => ({
    nickname: "",
    amount: "",
    features: [""],
    sequence: "",
    defaultMeetings: "",
    zoomCallsUnlimited: false,
    addOnMeetingPrice: "",
    // Pooled inbox SP5 (owner item 5, amended): what the apps show and Apple sells.
    tier: "",
    description: "",
    appleProductId: "",
});

const toInputString = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    return typeof value === "string" ? value : String(value);
};

const normalizeFeatureList = (rawFeatures) => {
    if (Array.isArray(rawFeatures) && rawFeatures.length) {
        return rawFeatures;
    }
    if (typeof rawFeatures === "string" && rawFeatures.trim()) {
        return rawFeatures
            .split(/[|,\n]/)
            .map((item) => item.trim())
            .filter(Boolean);
    }
    return [""];
};

const mapPlanToFormState = (plan) => {
    if (!plan) {
        return createInitialFormState();
    }
    const metadata = plan?.metadata || plan?.product?.metadata || {};
    const featuresSource = plan?.features ?? metadata?.features ?? plan?.stripe?.metadata?.features ?? [];
    const amountValue = plan?.amount ?? (typeof plan?.stripe?.unit_amount === "number" ? plan.stripe.unit_amount / 100 : metadata?.price);
    const zoomCallsUnlimited = Boolean(plan?.zoomCallsUnlimited ?? metadata?.zoomCallsUnlimited);
    
    return {
        nickname: toInputString(plan?.nickname || plan?.stripe?.nickname || metadata?.nickname || ""),
        amount: toInputString(amountValue),
        features: normalizeFeatureList(featuresSource),
        sequence: toInputString(plan?.sequence ?? metadata?.sequence ?? ""),
        defaultMeetings: toInputString(
            zoomCallsUnlimited
                ? 0
                : (plan?.defaultMeetings ?? metadata?.defaultMeetings ?? metadata?.default_meetings ?? "")
        ),
        zoomCallsUnlimited,
        addOnMeetingPrice: toInputString(plan?.addOnMeetingPrice ?? metadata?.addOnMeetingPrice ?? metadata?.addOnPrice ?? ""),
        tier: toInputString(plan?.tier ?? ""),
        description: toInputString(plan?.description ?? ""),
        appleProductId: toInputString(plan?.appleProductId ?? ""),
    };
};

/** Display formatting for package table currency columns */
function formatPackageTableCurrency(value) {
    if (typeof value === "number") {
        return value.toFixed(2);
    }
    if (typeof value === "string" && value) {
        const numeric = Number(value);
        return Number.isNaN(numeric) ? value : numeric.toFixed(2);
    }
    return "--";
}

/** Features list for admin table (differs from form normalizeFeatureList empty handling) */
function parseFeaturesForPackageTable(rawFeatures) {
    if (Array.isArray(rawFeatures)) {
        return rawFeatures;
    }
    if (typeof rawFeatures === "string") {
        return rawFeatures.split(/[|,\n]/).map((item) => item.trim()).filter(Boolean);
    }
    return [];
}

function resolvePackagePriceCandidates(pkg, metadata, stripeInfo) {
    const amountFromStripe =
        typeof stripeInfo?.unit_amount === "number" ? stripeInfo.unit_amount / 100 : null;
    const amountFromPkgField = typeof pkg?.amount === "number" ? pkg.amount : null;
    const amountFromMetadata = typeof metadata?.price === "number" ? metadata.price : null;
    return amountFromPkgField ?? amountFromStripe ?? amountFromMetadata;
}

function normalizeAdminPackageRow(pkg, index) {
    const metadata = pkg?.metadata || pkg?.product?.metadata || {};
    const stripeInfo = pkg?.stripe || {};
    const rawFeatures =
        pkg?.features || metadata?.features || stripeInfo?.metadata?.features || [];

    const featureList = parseFeaturesForPackageTable(rawFeatures);
    const price = resolvePackagePriceCandidates(pkg, metadata, stripeInfo);
    const addOnPrice = pkg?.addOnMeetingPrice ?? metadata?.addOnMeetingPrice ?? metadata?.addOnPrice;
    const planRecordId = pkg?._id || pkg?.id || pkg?.stripePriceId || pkg?.lookup_key || `pkg-${index}`;

    return {
        id: planRecordId,
        planRecordId,
        name: pkg?.nickname || stripeInfo?.nickname || pkg?.lookup_key || pkg?.product?.name || "Package",
        price: formatPackageTableCurrency(price),
        defaultMeetings: pkg?.defaultMeetings ?? metadata?.defaultMeetings ?? metadata?.default_meetings ?? "-",
        zoomCallsUnlimited: Boolean(pkg?.zoomCallsUnlimited ?? metadata?.zoomCallsUnlimited ?? false),
        addOnPrice: formatPackageTableCurrency(addOnPrice),
        features: featureList,
        sequence: pkg?.sequence ?? metadata?.sequence,
    };
}

function isPackageFormValueEmpty(value) {
    return (
        value === "" ||
        value === null ||
        value === undefined ||
        (typeof value === "string" && !value.trim())
    );
}

function validateRequiredPositiveNumber(value, emptyMessage, invalidMessage) {
    if (isPackageFormValueEmpty(value)) return emptyMessage;
    const num = Number(value);
    if (Number.isNaN(num) || num <= 0) return invalidMessage;
    return undefined;
}

function validateRequiredNonNegativeNumber(value, emptyMessage, invalidMessage) {
    if (isPackageFormValueEmpty(value)) return emptyMessage;
    const num = Number(value);
    if (Number.isNaN(num) || num < 0) return invalidMessage;
    return undefined;
}

function packageFormFieldErrorsNickname(values) {
    if (!values.nickname?.trim()) return { nickname: "Package name is required" };
    return {};
}

function packageFormFieldErrorsAmount(values) {
    const msg = validateRequiredPositiveNumber(
        values.amount,
        "Amount is required",
        "Amount must be greater than 0"
    );
    return msg ? { amount: msg } : {};
}

function packageFormFieldErrorsSequence(values) {
    const msg = validateRequiredPositiveNumber(
        values.sequence,
        "Sequence is required",
        "Sequence must be greater than 0"
    );
    return msg ? { sequence: msg } : {};
}

function packageFormFieldErrorsDefaultMeetings(values) {
    if (values.zoomCallsUnlimited) return {};
    const msg = validateRequiredNonNegativeNumber(
        values.defaultMeetings,
        "Default meetings is required",
        "Default meetings cannot be negative"
    );
    return msg ? { defaultMeetings: msg } : {};
}

function packageFormFieldErrorsAddOn(values) {
    const msg = validateRequiredNonNegativeNumber(
        values.addOnMeetingPrice,
        "Add-on price is required",
        "Add-on price cannot be negative"
    );
    return msg ? { addOnMeetingPrice: msg } : {};
}

function packageFormFieldErrorsFeatures(values) {
    const nonEmpty = (values.features || []).filter((feature) => feature?.trim());
    if (!nonEmpty.length) return { features: "At least one feature is required" };
    return {};
}

export function validatePackageManagementForm(values) {
    return {
        ...packageFormFieldErrorsNickname(values),
        ...packageFormFieldErrorsAmount(values),
        ...packageFormFieldErrorsSequence(values),
        ...packageFormFieldErrorsDefaultMeetings(values),
        ...packageFormFieldErrorsAddOn(values),
        ...packageFormFieldErrorsFeatures(values),
    };
}

// No `setupFeeEnable` / `setupFeePrice`. The setup fee is one platform-level
// amount (backend SEC-110 / INT-046; its never-mounted admin screen,
// ChangeSubscriptionPrice.jsx, was deleted in SP7); the per-plan fields are still accepted and stored by the API but no
// billing path reads them, so the controls that set them here only ever produced
// a number an admin could see and a customer would never be charged.
export function buildPackageSavePayload(values) {
    return {
        nickname: values.nickname.trim(),
        amount: Number(values.amount),
        currency: "USD",
        features: values.features.map((feature) => feature.trim()).filter(Boolean),
        sequence: Number(values.sequence),
        defaultMeetings: values.zoomCallsUnlimited ? 0 : Number(values.defaultMeetings),
        zoomCallsUnlimited: Boolean(values.zoomCallsUnlimited),
        addOnMeetingPrice: Number(values.addOnMeetingPrice),
        // SP5: a tier only when one is chosen (the server accepts the four
        // tier names or none); an empty App Store id is stored as none.
        ...(values.tier ? { tier: values.tier } : {}),
        description: (values.description || "").trim(),
        appleProductId: (values.appleProductId || "").trim() || null,
    };
}

// SP5 (owner item 5, amended): the Packages screen's Stripe status and the
// notices list for what the backend cannot do by itself, from
// GET /billing/admin/catalog. Exported for tests.
export function catalogNoticeText(notice, plansById) {
    const name = plansById[notice.planId]?.name || "";
    if (notice.code === "older_price_subscribers") return ADMIN_NOTICE_OLDER_PRICE(notice.count, name);
    if (notice.code === "apple_product_missing") return ADMIN_NOTICE_APPLE_MISSING(name);
    if (notice.code === "stripe_webhook_incomplete") return ADMIN_NOTICE_WEBHOOK;
    if (notice.code === "currency_mismatch") return ADMIN_NOTICE_CURRENCY_MISMATCH;
    if (notice.code === "apple_intro_offer_unverified") return ADMIN_NOTICE_APPLE_INTRO_UNVERIFIED(notice.productId);
    if (notice.code === "stripe_unreachable") return ADMIN_NOTICE_STRIPE_UNREACHABLE;
    if (notice.code === "stripe_tax_unchecked") return ADMIN_NOTICE_STRIPE_TAX_UNCHECKED;
    return null;
}

function StripeStatusLine({ row }) {
    if (!row) return null;
    const synced = row.stripeStatus === "synced";
    return (
        <p className="tw-m-0 tw-text-xs tw-font-semibold tw-text-text-muted">
            {ADMIN_STRIPE_STATUS}: <b className={synced ? "tw-text-success" : "tw-text-danger"}>{synced ? ADMIN_SYNCED : ADMIN_NEEDS_ATTENTION}</b>
            {!synced && ADMIN_STATUS_REASONS[row.reason] && (
                <span className="tw-block tw-font-regular">{ADMIN_STATUS_REASONS[row.reason]}</span>
            )}
        </p>
    );
}

function buildEditingPlanIdentifiers(editingPlanId, activePlanMeta) {
    if (!editingPlanId) {
        return {};
    }
    const stripeLikeId =
        activePlanMeta?.stripePriceId ||
        activePlanMeta?.stripe?.id ||
        activePlanMeta?.id ||
        editingPlanId;
    return {
        planId: editingPlanId,
        id: editingPlanId,
        _id: editingPlanId,
        stripePriceId: stripeLikeId,
        priceId: stripeLikeId,
    };
}

async function savePackageRequest(editingPlanId, payload, identifiers) {
    if (editingPlanId) {
        return updatePlanAdmin({
            id: editingPlanId,
            data: {
                ...payload,
                ...identifiers,
            },
        });
    }
    return createPlanAdmin(payload);
}

function resolveModalPrimaryButtonLabel(isSubmitting, isEditMode) {
    if (isSubmitting) {
        return "Saving...";
    }
    return isEditMode ? "Update Package" : "Save Package";
}

function toggleZoomUnlimitedMeetings(setFieldValue, zoomCallsUnlimited) {
    const nextValue = !zoomCallsUnlimited;
    setFieldValue("zoomCallsUnlimited", nextValue);
    if (nextValue) {
        setFieldValue("defaultMeetings", 0);
    }
}

function FormToggleSwitch({ checked, onClick }) {
    return (
        <span className="tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-shrink-0">
            <input
                type="checkbox"
                role="switch"
                id="package-unlimited"
                checked={Boolean(checked)}
                onChange={onClick}
                className="tw-peer tw-absolute tw-inset-0 tw-z-10 tw-m-0 tw-h-full tw-w-full tw-cursor-pointer tw-opacity-0"
            />
            <span
                aria-hidden="true"
                className="tw-absolute tw-inset-0 tw-rounded-pill tw-bg-border tw-transition-colors tw-duration-fast peer-checked:tw-bg-text-accent peer-focus-visible:tw-outline peer-focus-visible:tw-outline-2 peer-focus-visible:tw-outline-offset-2 peer-focus-visible:tw-outline-focus-ring"
            />
            <span
                aria-hidden="true"
                className="tw-absolute tw-inset-y-0 tw-left-1 tw-my-auto tw-h-5 tw-w-5 tw-rounded-pill tw-bg-surface tw-transition-transform tw-duration-fast peer-checked:tw-translate-x-4"
            />
        </span>
    );
}

function PackageFeaturesFieldArraySection({ values, remove, push }) {
    return (
        <>
            {values.features.map((feature, index) => (
                <div className="tw-mb-2 tw-flex tw-items-center tw-gap-3" data-slot="feature-input-box" key={feature}>
                    <FormikField
                        as={Input}
                        type="text"
                        name={`features[${index}]`}
                        aria-label={`Feature ${index + 1}`}
                        placeholder="Enter feature"
                    />
                    <Button
                        type="button"
                        variant="icon"
                        iconOnly
                        aria-label="Remove feature"
                        className="tw-border-solid tw-shrink-0"
                        onClick={() => remove(index)}
                        disabled={values.features.length === 1}
                    >
                        <StarsIcon name="trash" className="tw-h-5 tw-w-5 tw-shrink-0" />
                    </Button>
                </div>
            ))}
            <Button
                type="button"
                variant="quiet"
                size="sm"
                className="tw-border-solid"
                onClick={() => push("")}
                disabled={values.features.some((feature) => !feature?.trim())}
            >
                + Add Feature
            </Button>
        </>
    );
}

function PackageManagementModalFormContent({ values, errors, touched, setFieldValue, isSubmitting, isEditMode, handleClose }) {
    const modalPrimaryButtonLabel = resolveModalPrimaryButtonLabel(isSubmitting, isEditMode);
    const fieldError = (name) => (getIn(touched, name) ? getIn(errors, name) : undefined);
    return (
        <Form>
            <div className="tw-grid tw-gap-x-4 md:tw-grid-cols-2">
                <Field label="Package Name" id="package-name" error={fieldError("nickname")} className="md:tw-col-span-2">
                    <FormikField as={Input} type="text" name="nickname" placeholder="Enter package name" />
                </Field>
                <div className="tw-mb-4 tw-grid tw-min-w-0 tw-gap-2 md:tw-col-span-2 md:tw-w-1/2">
                    <label htmlFor="package-amount" className="tw-text-sm tw-font-semibold tw-text-text">Price</label>
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <FormikField
                            as={Input}
                            type="number"
                            id="package-amount"
                            name="amount"
                            placeholder="Enter amount"
                            invalid={Boolean(fieldError("amount"))}
                        />
                        <span className="tw-shrink-0 tw-whitespace-nowrap tw-text-sm tw-text-text-muted">Per Month</span>
                    </div>
                    {fieldError("amount") && <p className="tw-m-0 tw-text-sm tw-text-danger">{fieldError("amount")}</p>}
                </div>
                <div className="tw-mb-4 tw-grid tw-content-start tw-gap-2">
                    <label htmlFor="package-unlimited" className="tw-text-sm tw-font-semibold tw-text-text">Unlimited Meetings</label>
                    <FormToggleSwitch
                        checked={values.zoomCallsUnlimited}
                        onClick={() => toggleZoomUnlimitedMeetings(setFieldValue, values.zoomCallsUnlimited)}
                    />
                </div>
                <Field label="Default Meetings" id="package-default-meetings" error={fieldError("defaultMeetings")}>
                    <FormikField
                        as={Input}
                        type="number"
                        name="defaultMeetings"
                        placeholder="Enter value"
                        disabled={values.zoomCallsUnlimited}
                    />
                </Field>
                <Field label="Sequence" id="package-sequence" error={fieldError("sequence")}>
                    <FormikField as={Input} type="number" name="sequence" placeholder="Enter value" />
                </Field>
                <Field label="Add-on Meeting Price" id="package-add-on" error={fieldError("addOnMeetingPrice")}>
                    <FormikField as={Input} type="number" name="addOnMeetingPrice" placeholder="Enter Price" />
                </Field>
                <Field label={ADMIN_TIER} id="package-tier">
                    <FormikField as={Select} name="tier">
                        <option value="">{ADMIN_TIER_NONE}</option>
                        {Object.entries(ADMIN_TIERS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </FormikField>
                </Field>
                <Field label={ADMIN_APPLE_PRODUCT} id="package-apple-product">
                    <FormikField as={Input} type="text" name="appleProductId" />
                </Field>
                <Field label={ADMIN_DESCRIPTION} id="package-description" className="md:tw-col-span-2">
                    <FormikField as={Input} type="text" name="description" maxLength={120} />
                </Field>
                <div className="tw-mb-4 md:tw-col-span-2">
                    <p className="tw-m-0 tw-mb-2 tw-text-sm tw-font-semibold tw-text-text">Features List</p>
                    <FieldArray name="features">
                        {({ remove, push }) => (
                            <PackageFeaturesFieldArraySection values={values} remove={remove} push={push} />
                        )}
                    </FieldArray>
                    {typeof errors.features === "string" && touched.features && (
                        <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-danger">{errors.features}</p>
                    )}
                </div>
            </div>
            <div className="tw-flex tw-justify-end tw-gap-3">
                <Button type="button" variant="quiet" className="tw-border-solid" onClick={handleClose} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                    {modalPrimaryButtonLabel}
                </Button>
            </div>
        </Form>
    );
}

const PackageManagement = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [show, setShow] = useState(false);
    const [packages, setPackages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState("");
    const [formInitialValues, setFormInitialValues] = useState(createInitialFormState);
    const [editingPlanId, setEditingPlanId] = useState(null);
    const [activePlanMeta, setActivePlanMeta] = useState(null);
    const [isPlanDetailsLoading, setIsPlanDetailsLoading] = useState(false);
    const [confirmDocumentDeletePopup, setConfirmDocumentDeletePopup] = useState(false);
    const [packageIdToDelete, setPackageIdToDelete] = useState(null);
    const [catalog, setCatalog] = useState({ plans: [], notices: [] });

    const documentDeletePopupClose = () => {
        setConfirmDocumentDeletePopup(false);
        setPackageIdToDelete(null);
    };

    const handleDelete = async () => {
        if (!packageIdToDelete) {
            enqueueSnackbar("Unable to delete package. Missing identifier.", { variant: "error" });
            return;
        }
        try {
            const response = await removePackage(packageIdToDelete);
            if (response?.status >= 200 && response?.status < 300) {
                enqueueSnackbar("Package deleted successfully.", { variant: "success" });
                documentDeletePopupClose();
                fetchPackages();
            } else {
                enqueueSnackbar(response?.response?.data?.message || "Unable to delete package.", { variant: "error" });
            }
        } catch (error) {
            enqueueSnackbar("Unable to delete package.", { variant: "error" });
            console.error("Failed to delete package", error);
        }
    };


    const openDeletePopup = (packageId) => {
        setPackageIdToDelete(packageId);
        setConfirmDocumentDeletePopup(true);
    };

    const resetForm = () => {
        setFormInitialValues(createInitialFormState());
    };

    const handleClose = () => {
        setShow(false);
        resetForm();
        setEditingPlanId(null);
        setActivePlanMeta(null);
        setIsPlanDetailsLoading(false);
    };
    const handleShow = () => {
        resetForm();
        setEditingPlanId(null);
        setActivePlanMeta(null);
        setIsPlanDetailsLoading(false);
        setShow(true);
    };

    const handleEditPackage = async (planId) => {
        if (!planId) {
            enqueueSnackbar("Unable to edit this package. Missing identifier.", { variant: "error" });
            return;
        }
        resetForm();
        setEditingPlanId(planId);
        setActivePlanMeta(null);
        setShow(true);
        setIsPlanDetailsLoading(true);
        try {
            const response = await getSinglePlansAdmin(planId);
            if (response?.status === 200 && response?.data?.data) {
                const planDetails = response.data.data;
                setActivePlanMeta(planDetails);
                setFormInitialValues(mapPlanToFormState(planDetails));
            } else {
                enqueueSnackbar("Unable to load package details.", { variant: "error" });
                handleClose();
            }
        } catch (error) {
            enqueueSnackbar("Unable to load package details.", { variant: "error" });
            console.error("Failed to fetch plan details", error);
            handleClose();
        } finally {
            setIsPlanDetailsLoading(false);
        }
    };

    const fetchPackages = useCallback(async () => {
        setIsLoading(true);
        setFetchError("");
        try {
            const response = await getAllPlansAdmin();
            if (response?.status === 200 && Array.isArray(response?.data?.data)) {
                setPackages(response.data.data);
            } else {
                setFetchError("Unable to fetch packages. Please try again.");
            }
        } catch (error) {
            setFetchError("Unable to fetch packages. Please try again.");
            console.error("Failed to load packages", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // A failed status read leaves the column and the notices empty; the
    // catalog itself still shows.
    const fetchCatalog = useCallback(async () => {
        try {
            const response = await getBillingCatalog();
            setCatalog(response.data);
        } catch (_) {
            setCatalog({ plans: [], notices: [] });
        }
    }, []);

    useEffect(() => {
        fetchPackages();
        fetchCatalog();
    }, [fetchPackages, fetchCatalog]);

    const catalogById = useMemo(
        () => Object.fromEntries(catalog.plans.map((row) => [row.id, row])),
        [catalog]
    );

    const normalizedPackages = useMemo(
        () => packages.map((pkg, index) => normalizeAdminPackageRow(pkg, index)),
        [packages]
    );

    const handleSavePackage = async (values, { setSubmitting, resetForm }) => {
        const payload = buildPackageSavePayload(values);
        const identifiers = buildEditingPlanIdentifiers(editingPlanId, activePlanMeta);
        const saveVerb = editingPlanId ? "update" : "create";

        try {
            const response = await savePackageRequest(editingPlanId, payload, identifiers);
            const isSuccess = response?.status >= 200 && response?.status < 300;
            if (isSuccess) {
                enqueueSnackbar(
                    editingPlanId ? "Package updated successfully." : "Package created successfully.",
                    { variant: "success" }
                );
                resetForm({ values: createInitialFormState() });
                handleClose();
                fetchPackages();
                fetchCatalog();
            } else {
                enqueueSnackbar(
                    response?.response?.data?.message || `Unable to ${saveVerb} package.`,
                    { variant: "error" }
                );
            }
        } catch (error) {
            enqueueSnackbar(`Unable to ${saveVerb} package.`, { variant: "error" });
            console.error("Failed to save package", error);
        } finally {
            setSubmitting(false);
        }
    };

    const isEditMode = Boolean(editingPlanId);

    return (
        <Page>
            <PageHead
                title="Packages Management"
                tools={
                    <Button type="button" onClick={handleShow}>
                        <StarsIcon name="plus" className="tw-h-4 tw-w-4 tw-shrink-0" />
                        Add New Package
                    </Button>
                }
            />
            {isLoading ? (
                <Loader />
            ) : (
                <>
                    {fetchError && <p role="alert" className="tw-mb-4 tw-rounded-md tw-bg-overlay-danger-600/10 tw-px-4 tw-py-2 tw-text-sm tw-text-danger">{fetchError}</p>}
                    {catalog.notices.length > 0 && (
                        <Panel title={ADMIN_NOTICES_TITLE} role="region" aria-label={ADMIN_NOTICES_TITLE} aria-labelledby={undefined} className="tw-mb-6">
                            <ul className="tw-m-0 tw-pl-4">
                                {catalog.notices.map((notice) => (
                                    <li className="tw-text-sm" key={`${notice.code}-${notice.planId || ""}`}>
                                        {catalogNoticeText(notice, catalogById) ?? ADMIN_NOTICE_GENERIC}
                                    </li>
                                ))}
                            </ul>
                        </Panel>
                    )}
                    {normalizedPackages.length === 0 && !fetchError && (
                        <h2 className="tw-py-12 tw-text-center tw-text-xl tw-font-bold tw-text-text">
                            No packages found.
                        </h2>
                    )}
                    <div className="tw-grid tw-gap-6 md:tw-grid-cols-2 lg:tw-grid-cols-3">
                        {normalizedPackages.map((pkg) => (
                            <Panel as="div" data-slot="admin-package-card" className="tw-flex tw-flex-col tw-justify-between tw-gap-6" key={pkg.id}>
                                <div className="tw-grid tw-gap-4">
                                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                                        <div>
                                            <h6 className="tw-m-0 tw-mb-1 tw-text-lg tw-font-bold tw-text-text">{pkg.name}</h6>
                                            <p className="tw-m-0 tw-text-text">
                                                <b className="tw-text-2xl tw-font-bold tw-tabular-nums">${pkg.price || "--"}</b>
                                                <span className="tw-text-xs tw-text-text-muted">/month</span>
                                            </p>
                                        </div>
                                        <div className="tw-text-right">
                                            <p className="tw-m-0 tw-mb-1 tw-text-xs tw-text-text-muted">Default Meetings</p>
                                            <p className="tw-m-0 tw-text-lg tw-font-bold tw-text-text">
                                                {pkg.zoomCallsUnlimited ? "Unlimited" : (pkg.defaultMeetings ?? "-")}
                                            </p>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="tw-m-0 tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-text-text-muted">Features:</p>
                                        {pkg.features?.length ? (
                                            <ul className="tw-m-0 tw-mb-3 tw-pl-4">
                                                {pkg.features.map((feature) => (
                                                    <li className="tw-text-sm" key={feature}>
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="tw-m-0 tw-mb-3 tw-text-sm tw-text-text-muted">No features listed.</p>
                                        )}
                                        <p className="tw-m-0 tw-mb-2 tw-text-xs tw-font-semibold tw-text-text-muted">
                                            Add-on Meeting Price : ${pkg.addOnPrice || "--"}/Each Slot
                                        </p>
                                        <StripeStatusLine row={catalogById[pkg.planRecordId]} />
                                    </div>
                                </div>

                                <div className="tw-flex tw-gap-3">
                                    <Button
                                        type="button"
                                        variant="quiet"
                                        size="sm"
                                        className="tw-border-solid tw-flex-1"
                                        onClick={() => handleEditPackage(pkg.planRecordId)}
                                        disabled={!pkg.planRecordId}
                                    >
                                        Edit Package
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        className="tw-flex-1"
                                        onClick={() => openDeletePopup(pkg.planRecordId)}
                                        disabled={!pkg.planRecordId}
                                    >
                                        Delete Package
                                    </Button>
                                </div>
                            </Panel>
                        ))}
                    </div>
                </>
            )}

            <Dialog
                open={show}
                onOpenChange={(open) => { if (!open) handleClose(); }}
            >
                <DialogContent
                    onEscapeKeyDown={(e) => e.preventDefault()}
                    className="tw-max-h-full tw-max-w-2xl tw-overflow-y-auto"
                >
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                        <DialogTitle>{isEditMode ? "Edit Package" : "Add Package"}</DialogTitle>
                        <Button
                            variant="icon"
                            size="sm"
                            iconOnly
                            aria-label="Close"
                            className="tw-border-solid tw-shrink-0"
                            onClick={handleClose}
                        >
                            <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
                        </Button>
                    </div>

                    <div className="tw-mt-4">
                        {isPlanDetailsLoading ? (
                            <Loader />
                        ) : (
                            <Formik
                                enableReinitialize
                                initialValues={formInitialValues}
                                validate={validatePackageManagementForm}
                                onSubmit={handleSavePackage}
                                validateOnMount={false}
                            >
                                {(formikProps) => (
                                    <PackageManagementModalFormContent
                                        {...formikProps}
                                        isEditMode={isEditMode}
                                        handleClose={handleClose}
                                    />
                                )}
                            </Formik>
                        )}
                    </div>
                </DialogContent>
            </Dialog>


            {/* confirm delete popup */}
            <Dialog
                open={confirmDocumentDeletePopup}
                onOpenChange={(open) => { if (!open) documentDeletePopupClose(); }}
            >
                <DialogContent
                    onInteractOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                    className="tw-text-center"
                >
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                        <span />
                        <Button
                            variant="icon"
                            size="sm"
                            iconOnly
                            aria-label="Close"
                            className="tw-border-solid tw-shrink-0"
                            onClick={documentDeletePopupClose}
                        >
                            <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
                        </Button>
                    </div>
                    <div className="tw-flex tw-justify-center tw-text-text-accent">
                        <StarsIcon name="trash" className="tw-h-10 tw-w-10 tw-shrink-0" />
                    </div>

                    <DialogDescription className="tw-mb-2 tw-mt-3 tw-text-md">You are attempting to delete this document.</DialogDescription>
                    <DialogTitle>Are you sure?</DialogTitle>
                    <div className="tw-mt-6 tw-flex tw-justify-center">
                        <Button onClick={handleDelete}>
                            <StarsIcon name="check" className="tw-h-4 tw-w-4 tw-shrink-0" />
                            Confirm
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </Page>
    );
};

export default PackageManagement;